/**
 * The payment page — /pay (payment recovery, sw-ev-api
 * docs/PAYMENT_RECOVERY.md).
 *
 * A customer whose session is charging (or has charged) with no card
 * authorization enters their card again here, for the SAME session. It is
 * GreenCharge's own page: not a gateway-hosted page, and nothing is stored
 * for reuse.
 *
 * The link token never reaches this component. api/payLinkApi.js reads it
 * from pay/payLinkToken.js and sends it in a header; this file only says
 * when the token can be forgotten.
 *
 * What the screen says about money always comes from the API: the amount is
 * `amount.display`, and "paid" is shown only for the outcome `paid`.
 *
 * Phases:
 *   loading      first read of the link
 *   missing      opened with no link
 *   dead         the link is expired / replaced / unknown
 *   unavailable  the link could not be read right now (retry)
 *   payable      the card form (hold or final amount)
 *   working      the card has been sent; waiting for the answer
 *   method       3DS method step (hidden frame), then "continue"
 *   challenge    3DS challenge (the bank's screen), message + polling
 *   waiting      an attempt is in flight with nothing to show; polling
 *   slow         the 3DS exchange outlasted the page's patience
 *   secured | authorized | paid | closed   results
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GreenChargeLogo from '../../components/Common/GreenChargeLogo.jsx';
import ThemeToggle from '../../theme/ThemeToggle.jsx';
import Button from '../../components/ui/Button/Button.jsx';
import Card from '../../components/ui/Card/Card.jsx';
import Badge from '../../components/ui/Badge/Badge.jsx';
import {
  continuePayAttempt, getPayAttempt, getPayContext, payLinkApiOrigin, startPayAttempt,
} from '../../api/payLinkApi.js';
import { clearPayLinkToken, hasPayLinkToken } from '../../pay/payLinkToken.js';
import { v4 as uuidv4 } from '../../api/uuid.js';
import PayCardForm from './PayCardForm.jsx';
import { COMPLETE_MESSAGE_TYPE, PAY_TIMINGS } from './payTimings.js';
import { PayChallengeFrame, PayMethodFrame } from './PayThreeDSFrames.jsx';
import './PayPage.less';

const FINAL_PHASES = ['secured', 'authorized', 'paid', 'closed', 'dead', 'missing'];
const POLLED_PHASES = ['waiting', 'challenge', 'authorized'];

const NOTICES = {
  declined: {
    tone: 'danger',
    title: 'Your card was declined',
    body: 'Please check the card details, or try another card.',
  },
  failed: {
    tone: 'warning',
    title: 'The card authorization could not be completed',
    body: 'Please try again.',
  },
  timed_out: {
    tone: 'warning',
    title: 'Card verification was not completed in time',
    body: 'Please try again.',
  },
  released: {
    tone: 'info',
    title: 'The authorization was released',
    body: 'Nothing was taken from your card.',
  },
  rate_limited: {
    tone: 'warning',
    title: 'Too many attempts',
    body: 'Please wait a few minutes and try again.',
  },
  unconfirmed: {
    tone: 'warning',
    title: 'We could not confirm that request',
    body: 'Check your connection and try again.',
  },
};

const DEAD_LINKS = {
  expired: {
    title: 'This payment link has expired',
    body: 'A newer link may have been sent to you. Please use the most recent link in your email, or open the GreenCharge app.',
  },
  replaced: {
    title: 'This payment link has been replaced',
    body: 'A newer link may have been sent to you. Please use the most recent link in your email, or open the GreenCharge app.',
  },
  not_found: {
    title: 'We could not find this payment link',
    body: 'Please check that you opened the complete link from your email, or open the GreenCharge app.',
  },
};

const noticeForAttempt = (view) => {
  if (view?.outcome === 'declined') return 'declined';
  if (view?.outcome === 'released') return 'released';
  return view?.error === 'TIMED_OUT' ? 'timed_out' : 'failed';
};

function Shell({ phase, children }) {
  return (
    <div className="pay-shell" data-testid="pay-page" data-phase={phase}>
      <div className="pay-container">
        <header className="pay-brandbar">
          <GreenChargeLogo variant="wordmark" />
          <ThemeToggle className="pay-theme-toggle" />
        </header>
        {children}
        <p className="pay-footnote">
          Secure card payment for your GreenCharge charging session.
        </p>
      </div>
    </div>
  );
}

function NoticeBox({ kind }) {
  const notice = NOTICES[kind];
  if (!notice) return null;
  return (
    <div
      className={`pay-notice pay-notice--${notice.tone}`}
      role={notice.tone === 'info' ? 'status' : 'alert'}
      data-testid="pay-notice"
      data-notice={kind}
    >
      <p className="pay-notice-title">{notice.title}</p>
      <p className="pay-notice-body">{notice.body}</p>
    </div>
  );
}

function Busy({ title, children }) {
  return (
    <div className="pay-busy" role="status" aria-live="polite">
      <span className="pay-spinner" aria-hidden="true" />
      <h1 className="pay-title">{title}</h1>
      {children && <p className="pay-text">{children}</p>}
    </div>
  );
}

function Result({ tone = 'success', title, children, testId }) {
  return (
    <div className={`pay-result pay-result--${tone}`} role="status" data-testid={testId}>
      <span className="pay-result-mark" aria-hidden="true">{tone === 'success' ? '✓' : 'i'}</span>
      <h1 className="pay-title">{title}</h1>
      {children}
    </div>
  );
}

function SessionSummary({ session }) {
  if (!session) return null;
  const energy = Number.isFinite(session.energyKwh) ? session.energyKwh : null;
  return (
    <dl className="pay-session" data-testid="pay-session">
      <div className="pay-session-row">
        <dt>Session</dt>
        <dd className="pay-session-ref">{session.reference}</dd>
      </div>
      <div className="pay-session-row">
        <dt>Status</dt>
        <dd>
          {session.charging
            ? <Badge tone="success">Charging in progress</Badge>
            : <Badge tone="neutral">Session finished</Badge>}
        </dd>
      </div>
      {energy != null && (
        <div className="pay-session-row">
          <dt>{session.charging ? 'Energy so far' : 'Energy delivered'}</dt>
          <dd>{energy} kWh</dd>
        </div>
      )}
    </dl>
  );
}

export default function PayPage({ timings }) {
  const t = useMemo(() => ({ ...PAY_TIMINGS, ...timings }), [timings]);

  const [phase, setPhaseState] = useState('loading');
  const [context, setContext] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [notice, setNotice] = useState(null);
  const [apiField, setApiField] = useState(null);
  const [deadKind, setDeadKind] = useState('not_found');
  const [checking, setChecking] = useState(false);
  const [stillWaiting, setStillWaiting] = useState(false);
  // Remounts the card form, so it never keeps a card across attempts.
  const [formKey, setFormKey] = useState(0);

  const phaseRef = useRef('loading');
  const submitting = useRef(false);
  const pollNow = useRef(null);
  const mounted = useRef(true);

  const setPhase = useCallback((next) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  /** A dead or missing link ends the page. Returns true when it did. */
  const endOnLinkFailure = useCallback((res) => {
    if (res.code === 'NO_LINK') {
      setPhase('missing');
      return true;
    }
    if (res.linkState) {
      clearPayLinkToken();
      setDeadKind(res.linkState);
      setPhase('dead');
      return true;
    }
    return false;
  }, [setPhase]);

  /**
   * Show what the API says about an attempt. Returns 'reload' when the
   * attempt is over without an authorization: the caller then reads the
   * link again, because what is owed may have changed meanwhile.
   */
  const applyAttempt = useCallback((view) => {
    if (!view || typeof view !== 'object') return 'reload';
    // A late answer about a step never undoes a result already shown.
    if (view.outcome === 'processing' && FINAL_PHASES.includes(phaseRef.current)) return null;
    setAttempt(view);
    switch (view.outcome) {
      case 'processing':
        if (view.step === 'METHOD' && view.methodForm) setPhase('method');
        else if (view.step === 'CHALLENGE' && view.acsURL && view.cReq) setPhase('challenge');
        else setPhase('waiting');
        return null;
      case 'secured':
        clearPayLinkToken();
        setPhase('secured');
        return null;
      case 'paid':
        clearPayLinkToken();
        setPhase('paid');
        return null;
      case 'authorized':
        setPhase('authorized');
        return null;
      default: // declined | failed | released | anything unknown
        setNotice(noticeForAttempt(view));
        return 'reload';
    }
  }, [setPhase]);

  const loadContext = useCallback(async ({ keepNotice = false } = {}) => {
    setPhase('loading');
    const res = await getPayContext();
    if (!mounted.current) return;
    if (!res.ok) {
      if (!endOnLinkFailure(res)) setPhase('unavailable');
      return;
    }
    const data = res.data;
    switch (data.state) {
      case 'payable':
        if (!data.amount?.display || !['hold', 'final'].includes(data.mode)) {
          setPhase('unavailable');
          return;
        }
        setContext(data);
        if (!keepNotice && data.lastAttempt) setNotice(noticeForAttempt(data.lastAttempt));
        setFormKey((k) => k + 1);
        setPhase('payable');
        return;
      case 'processing':
      case 'authorized':
      case 'secured':
      case 'paid':
        setContext(data);
        if (data.state === 'processing') {
          // An in-flight attempt that has ended since is read again once.
          if (!data.attempt || applyAttempt(data.attempt) === 'reload') setPhase('unavailable');
          return;
        }
        setAttempt(data.attempt || null);
        if (data.state !== 'authorized') clearPayLinkToken();
        setPhase(data.state);
        return;
      case 'closed':
        clearPayLinkToken();
        setPhase('closed');
        return;
      default:
        setPhase('unavailable');
    }
  }, [applyAttempt, endOnLinkFailure, setPhase]);

  /** One answer from the attempt endpoints, wherever it came from. */
  const takeAttemptAnswer = useCallback(async (res, { quiet = false } = {}) => {
    if (!mounted.current) return;
    if (!res.ok) {
      if (endOnLinkFailure(res)) return;
      // A poll that fails says nothing about the payment: the next one asks again.
      if (!quiet) setPhase('slow');
      return;
    }
    if (applyAttempt(res.data) === 'reload') await loadContext({ keepNotice: true });
  }, [applyAttempt, endOnLinkFailure, loadContext, setPhase]);

  // First read.
  useEffect(() => {
    if (!hasPayLinkToken()) {
      setPhase('missing');
      return;
    }
    loadContext();
  }, [loadContext, setPhase]);

  const submitCard = useCallback(async (card) => {
    if (submitting.current) return;
    submitting.current = true;
    setNotice(null);
    setApiField(null);
    setPhase('working');
    // One key per submit: a repeat of this request returns the same attempt.
    const idempotencyKey = globalThis.crypto?.randomUUID?.() || uuidv4();
    const res = await startPayAttempt({ card, idempotencyKey });
    submitting.current = false;
    if (!mounted.current) return;
    if (res.ok) {
      if (applyAttempt(res.data) === 'reload') await loadContext({ keepNotice: true });
      return;
    }
    if (endOnLinkFailure(res)) return;
    switch (res.code) {
      case 'INVALID_CARD':
        setApiField(res.field || 'number');
        setFormKey((k) => k + 1);
        setPhase('payable');
        return;
      case 'ATTEMPT_IN_PROGRESS':
        if (res.attemptId) {
          setAttempt({ attemptId: res.attemptId, outcome: 'processing', step: 'STARTING' });
          await takeAttemptAnswer(await getPayAttempt(res.attemptId));
        } else {
          await loadContext();
        }
        return;
      case 'ALREADY_AUTHORIZED':
      case 'ALREADY_PAID':
      case 'NOTHING_TO_PAY':
        await loadContext();
        return;
      case 'RATE_LIMITED':
        setNotice('rate_limited');
        setFormKey((k) => k + 1);
        setPhase('payable');
        return;
      default:
        // No answer, or an error: whether an attempt exists is the API's to say.
        setNotice('unconfirmed');
        await loadContext({ keepNotice: true });
    }
  }, [applyAttempt, endOnLinkFailure, loadContext, setPhase, takeAttemptAnswer]);

  const attemptId = attempt?.attemptId ?? null;

  // 3DS method step: give the hidden frame its time, then continue.
  useEffect(() => {
    if (phase !== 'method' || attemptId == null) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const res = await continuePayAttempt(attemptId);
      if (!cancelled) await takeAttemptAnswer(res);
    }, t.methodWaitMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phase, attemptId, t.methodWaitMs, takeAttemptAnswer]);

  // Poll the attempt while a step is open. The outcome it returns is the
  // only truth; the 3DS message below merely asks for an earlier poll.
  useEffect(() => {
    if (!POLLED_PHASES.includes(phase) || attemptId == null) return undefined;
    let stopped = false;
    let inFlight = false;
    const tick = async () => {
      if (stopped || inFlight) return;
      inFlight = true;
      const res = await getPayAttempt(attemptId);
      inFlight = false;
      if (!stopped) await takeAttemptAnswer(res, { quiet: true });
    };
    pollNow.current = tick;
    const watchingAuthorized = phase === 'authorized';
    const interval = setInterval(tick, watchingAuthorized ? t.authorizedPollMs : t.pollMs);
    const deadline = setTimeout(() => {
      stopped = true;
      clearInterval(interval);
      // An authorized payment needs nothing from the customer: just stop watching.
      if (!watchingAuthorized) {
        setStillWaiting(false);
        setPhase('slow');
      }
    }, watchingAuthorized ? t.authorizedWatchMs : t.challengeTimeoutMs);
    return () => {
      stopped = true;
      pollNow.current = null;
      clearInterval(interval);
      clearTimeout(deadline);
    };
  }, [phase, attemptId, t.pollMs, t.authorizedPollMs, t.authorizedWatchMs, t.challengeTimeoutMs, takeAttemptAnswer, setPhase]);

  // The API's 3DS result page, inside the challenge frame, says the
  // challenge is over. Trusted only as far as "ask now", and only from the
  // API's own origin.
  useEffect(() => {
    if (phase !== 'challenge') return undefined;
    const apiOrigin = payLinkApiOrigin();
    const onMessage = (event) => {
      if (!apiOrigin || event.origin !== apiOrigin) return;
      if (event.data?.type !== COMPLETE_MESSAGE_TYPE) return;
      pollNow.current?.();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [phase]);

  const checkAgain = useCallback(async () => {
    if (attemptId == null) {
      await loadContext();
      return;
    }
    setChecking(true);
    const res = await getPayAttempt(attemptId);
    if (!mounted.current) return;
    setChecking(false);
    if (res.ok && res.data?.outcome === 'processing' && res.data.step !== 'METHOD') {
      // Still with the bank. Stay here rather than posting the challenge again.
      setAttempt(res.data);
      setStillWaiting(true);
      return;
    }
    if (!res.ok && !res.linkState && res.code !== 'NO_LINK') {
      setStillWaiting(true);
      return;
    }
    await takeAttemptAnswer(res);
  }, [attemptId, loadContext, takeAttemptAnswer]);

  // ── Render ────────────────────────────────────────────────────────

  let body;
  switch (phase) {
    case 'loading':
      body = <Busy title="Opening your payment…" />;
      break;

    case 'missing':
      body = (
        <Result tone="info" title="Open your payment link" testId="pay-missing">
          <p className="pay-text">
            This page opens from the payment link in your GreenCharge email, or from the
            GreenCharge app while you are charging.
          </p>
        </Result>
      );
      break;

    case 'dead': {
      const dead = DEAD_LINKS[deadKind] || DEAD_LINKS.not_found;
      body = (
        <Result tone="info" title={dead.title} testId="pay-dead">
          <p className="pay-text" data-link-state={deadKind}>{dead.body}</p>
        </Result>
      );
      break;
    }

    case 'unavailable':
      body = (
        <Result tone="info" title="We could not open this payment right now" testId="pay-unavailable">
          <p className="pay-text">Please check your connection and try again in a moment.</p>
          <Button fullWidth onClick={() => loadContext()} data-testid="pay-retry">Try again</Button>
        </Result>
      );
      break;

    case 'payable': {
      const hold = context.mode === 'hold';
      const amount = context.amount.display;
      body = (
        <>
          <h1 className="pay-title">{hold ? 'Confirm your card' : 'Pay for your charging session'}</h1>
          <SessionSummary session={context.session} />
          {hold ? (
            <div className="pay-amount" data-testid="pay-amount" data-mode="hold">
              <p className="pay-amount-label">Temporary authorization</p>
              <p className="pay-amount-value">{amount}</p>
              <p className="pay-text">
                This is a temporary hold of {amount} on your card, not a charge. When your
                session ends, only the actual cost of the session is taken and the rest of
                the hold is released.
              </p>
              <p className="pay-text pay-text--strong">
                You do not need to stop or restart charging.
              </p>
            </div>
          ) : (
            <div className="pay-amount" data-testid="pay-amount" data-mode="final">
              <p className="pay-amount-label">Amount to pay</p>
              <p className="pay-amount-value">{amount}</p>
              <p className="pay-text">
                You will be charged exactly {amount} for this finished charging session.
              </p>
            </div>
          )}
          <NoticeBox kind={notice} />
          <PayCardForm
            key={formKey}
            submitLabel={hold ? 'Confirm card' : `Pay ${amount}`}
            apiField={apiField}
            onSubmit={submitCard}
          />
        </>
      );
      break;
    }

    case 'working':
      body = <Busy title="Checking your card…">Please keep this page open.</Busy>;
      break;

    case 'method':
      body = (
        <>
          <Busy title="Checking your card…">Please keep this page open.</Busy>
          <PayMethodFrame methodForm={attempt?.methodForm} />
        </>
      );
      break;

    case 'waiting':
      body = <Busy title="Waiting for your bank…">Please keep this page open.</Busy>;
      break;

    case 'challenge':
      body = (
        <>
          <h1 className="pay-title">Verify with your bank</h1>
          <p className="pay-text">
            Your bank is asking you to confirm this card. Complete the step below; this
            page updates by itself.
          </p>
          <PayChallengeFrame
            acsURL={attempt?.acsURL}
            cReq={attempt?.cReq}
            threeDSSessionData={attempt?.threeDSSessionData}
          />
        </>
      );
      break;

    case 'slow':
      body = (
        <Result tone="info" title="This is taking longer than expected" testId="pay-slow">
          <p className="pay-text">
            We have not heard back about your card yet. We will not show a payment as
            made until it is confirmed.
          </p>
          {stillWaiting && (
            <p className="pay-text" role="status" data-testid="pay-still-waiting">
              Still waiting. Please try again in a moment.
            </p>
          )}
          <Button fullWidth busy={checking} onClick={checkAgain} data-testid="pay-check-again">
            Check again
          </Button>
        </Result>
      );
      break;

    case 'secured':
      body = (
        <Result title="Card confirmed" testId="pay-secured">
          <p className="pay-text">
            Charging continues. We will take only the actual cost when your session ends.
          </p>
        </Result>
      );
      break;

    case 'authorized':
      body = (
        <Result title="Card authorized" testId="pay-authorized">
          <p className="pay-text">
            We are completing your payment; you do not need to do anything else.
          </p>
        </Result>
      );
      break;

    case 'paid':
      body = (
        <Result title="Payment received" testId="pay-paid">
          {attempt?.outcome === 'paid' && attempt.captured?.display && (
            <p className="pay-amount-value" data-testid="pay-captured">{attempt.captured.display}</p>
          )}
          <p className="pay-text">Thank you. Your charging session is paid.</p>
        </Result>
      );
      break;

    case 'closed':
    default:
      body = (
        <Result tone="info" title="There is nothing to pay" testId="pay-closed">
          {notice === 'released' && <p className="pay-text">{NOTICES.released.body}</p>}
          <p className="pay-text">This payment request is closed. You do not need to do anything.</p>
        </Result>
      );
  }

  return (
    <Shell phase={phase}>
      <Card className="pay-card">{body}</Card>
      {['secured', 'authorized', 'paid', 'closed'].includes(phase) && (
        <p className="pay-back">
          <a href="/" className="pay-back-link">Back to GreenCharge</a>
        </p>
      )}
    </Shell>
  );
}
